/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 OutlinedInput, NS/Tractie component overrides.
// All MUI v4 style classes are declared explicitly (no implicit inheritance on MUI defaults).
// Mode-split: notchedOutline and root hover border-colors differ between light and dark.
//
// Token references:
//   - colorSecondary: MUI color="secondary" historically collapsed focus border to --ns-color-border-focus;
//     post-Tractie focus-strategy verschuift naar root-outline, dus colorSecondary heeft
//     geen focus-shift meer nodig).
//   - Webkit-autofill (P3 NS-extension) per TOKEN-ARCHITECTURE.md §7.16.
//   - Focus-strategy: outline op OutlinedInput root (niet inner input) per Tractie input-pattern
//     in node_modules/@tractie/components/src/custom-elements/input/src/input.css.js.
//     Eén focus-indicator (outline 2px op root, inset offset -2px) plus elevated-1 box-shadow.
//     Notched-border kleurt NIET mee bij focus (geen border-width-shift, geen visuele dubbele ring).

const autofillReset = {
  '&:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px var(--ns-color-bg-input-autofill) inset',
    WebkitTextFillColor: 'var(--ns-color-fg-default)',
    borderRadius: 'inherit',
    caretColor: 'var(--ns-color-fg-default)',
  },
} as const;

export const MuiOutlinedInputLightOverrides = {
  adornedEnd: {
    paddingRight: 14,
  },

  adornedStart: {
    paddingLeft: 14,
  },

  // colorSecondary: leeg gelaten — focus-strategie zit op root outline (Tractie),
  // niet meer op notched-border kleurshift (ground-rules.md "Focus").
  colorSecondary: {},

  disabled: {},

  error: {},

  focused: {},

  input: {
    ...autofillReset,
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    // T1.4 (2026-05-22): 7px 11px pariteit met Tractie internal padding (was 8px 12px).
    padding: '7px 11px',
    // T1.4 (2026-05-25 refactor): inner input h=38 zodat root (met border 1px
    // elk side, border-box) total = 38 + 2 = 40 → 39.6 effectief @ DPR.
    // Pariteit met Tractie/BUI total visible box h=39.60.
    height: '38px',
    boxSizing: 'border-box' as const,
    // T1.4 (2026-05-22): placeholder color pariteit via --ns-color-fg-placeholder
    // (was MUI default rgba(0,1,13,0.55) met opacity). Token-architectuur
    // gebruikt --ns-color-fg-placeholder; mocht visuele exacte match met
    // Tractie's #757575 nodig zijn, voeg een dedicated NS-token toe.
    '&::placeholder': {
      color: 'var(--ns-color-fg-placeholder)',
      opacity: 1,
    },
  },

  inputAdornedEnd: {
    paddingRight: 0,
  },

  inputAdornedStart: {
    paddingLeft: 0,
  },

  inputMarginDense: {
    paddingBottom: 10.5,
    paddingTop: 10.5,
  },

  root: {
    position: 'relative' as const,
    borderRadius: 6,
    backgroundColor: 'var(--ns-color-bg-input)',
    transition: 'none',
    minHeight: 39.6,
    // T1.4 (2026-05-25 refactor): Versimpel MUI compositie — border DIRECT op
    // root (single-element pattern, zoals Tractie/BUI). Voorheen leefde border
    // op fieldset.notchedOutline (positioned absolute child) → dual-element
    // complexiteit waar elements elkaar tegenwerken. Met fieldset display:none
    // (zie notchedOutline override) is root nu het enige border-element.
    border: '1px solid var(--ns-color-border-input-default)',
    boxSizing: 'border-box' as const,
    // Hover: change root borderColor (geen meer notchedOutline target).
    '&:hover, &.force-hover': {
      borderColor: 'var(--ns-color-border-input-hover)',
    },
    '@media (hover: none)': {
      '&:hover': {
        borderColor: 'var(--ns-color-border-input-default)',
      },
    },
    // Focus: change root borderColor + outline ring (Tractie focus-formule:
    // border-color blue + 2px outline ring, geen offset zodat ring direct
    // tegen border ligt voor clean look).
    '&$focused, &.force-focus-visible': {
      borderColor: 'var(--ns-color-border-focus)',
      outline:
        'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
      outlineOffset: 0,
    },
    // Error: change root borderColor.
    '&$error, &.force-error': {
      borderColor: 'var(--ns-color-border-error)',
    },
    // Compound focus+error: error wins op border en outline.
    '&$focused$error, &.force-focus-visible$error, &$focused.force-error, &.force-focus-visible.force-error':
      {
        borderColor: 'var(--ns-color-border-error)',
        outline:
          'var(--ns-border-width-medium) solid var(--ns-color-border-error)',
      },
    // Disabled: opacity 1 (pariteit Tractie+BUI) + houd default border-color.
    '&$disabled': {
      opacity: 1,
      borderColor: 'var(--ns-color-border-input-default)',
    },
  },

  inputMultiline: {
    padding: 0,
  },

  marginDense: {},

  multiline: {
    '&$marginDense': {
      paddingBottom: 10.5,
      paddingTop: 10.5,
    },
    // T1.4 (2026-05-22): 7px 11px pariteit met Tractie internal padding (was 8px 12px).
    padding: '7px 11px',
  },

  notchedOutline: {
    // T1.4 (2026-05-25 refactor): Hide fieldset volledig. Border verplaatst
    // naar root (single-element pattern, pariteit met Tractie/BUI clean
    // architecture). Notch + legend zijn niet meer nodig (label leeft buiten
    // de input-box per onze layout).
    display: 'none' as const,
  },
} as const;

// Dark palette variant. Border-colors flip via tokens (cluster 3 dark);
// autofill bg uses higher alpha for visibility on dark input-bg.
// Focus-shape (outline + offset) identiek aan light voor mode-pariteit.
export const MuiOutlinedInputDarkOverrides = {
  ...MuiOutlinedInputLightOverrides,
  input: {
    ...autofillReset,
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    // T1.4 (2026-05-22): 7px 11px pariteit met Tractie internal padding (was 8px 12px).
    padding: '7px 11px',
    // T1.4 (2026-05-25 refactor): inner input h=38 zodat root (met border 1px
    // elk side, border-box) total = 38 + 2 = 40 → 39.6 effectief @ DPR.
    // Pariteit met Tractie/BUI total visible box h=39.60.
    height: '38px',
    boxSizing: 'border-box' as const,
    // T1.4 (2026-05-22): placeholder color pariteit via --ns-color-fg-placeholder
    // (was MUI default rgba(0,1,13,0.55) met opacity). Token-architectuur
    // gebruikt --ns-color-fg-placeholder; mocht visuele exacte match met
    // Tractie's #757575 nodig zijn, voeg een dedicated NS-token toe.
    '&::placeholder': {
      color: 'var(--ns-color-fg-placeholder)',
      opacity: 1,
    },
  },
  notchedOutline: {
    // T1.4 (2026-05-25 refactor): Hide fieldset volledig. Border verplaatst
    // naar root (single-element pattern, pariteit met Tractie/BUI clean
    // architecture). Notch + legend zijn niet meer nodig (label leeft buiten
    // de input-box per onze layout).
    display: 'none' as const,
  },
  root: {
    // T1.4 (2026-05-25 refactor) dark mode: single-element border op root,
    // notchedOutline fieldset hidden. Pariteit met light + Tractie/BUI.
    position: 'relative' as const,
    borderRadius: 6,
    backgroundColor: 'var(--ns-color-bg-input)',
    transition: 'none',
    minHeight: 39.6,
    border: '1px solid var(--ns-color-border-input-default)',
    boxSizing: 'border-box' as const,
    '&:hover, &.force-hover': {
      borderColor: 'var(--ns-color-border-input-hover)',
    },
    '@media (hover: none)': {
      '&:hover': {
        borderColor: 'var(--ns-color-border-input-default)',
      },
    },
    '&$focused, &.force-focus-visible': {
      borderColor: 'var(--ns-color-border-focus)',
      outline:
        'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
      outlineOffset: 0,
    },
    '&$error, &.force-error': {
      borderColor: 'var(--ns-color-border-error)',
    },
    '&$focused$error, &.force-focus-visible$error, &$focused.force-error, &.force-focus-visible.force-error':
      {
        borderColor: 'var(--ns-color-border-error)',
        outline:
          'var(--ns-border-width-medium) solid var(--ns-color-border-error)',
      },
    '&$disabled': {
      opacity: 1,
      borderColor: 'var(--ns-color-border-input-default)',
    },
  },
} as const;
